import { Colors, Lightning, Utils } from "@lightningjs/sdk";
import theme from "../../../lib/theme";
import { Theme, themeManager } from "../../configurations/Theme/ThemeManager";
import { MaterialIcon } from "../../data-display/MaterialIcon/MaterialIcon";

interface KeyData {
    origin: string; //raw string provided in layout
    key: string; //single character or string
    label: string; //what the key component should show on screen
    action: string; //what kind of action the key should fire on key press
}

interface KeyboardKeyTemplateSpec extends Lightning.Component.TemplateSpec {
    Background: object;
    Label: object;
    Icon: typeof MaterialIcon;
}

export class KeyboardKey
    extends Lightning.Component<KeyboardKeyTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<KeyboardKeyTemplateSpec>
{
    _themeCallback!: (theme: Theme | undefined) => void;
    static override _template(): Lightning.Component.Template<KeyboardKeyTemplateSpec> {
        return {
            collision: true,
            zIndex: 10,
            Background: {
                w: (w) => w,
                h: (h) => h,
                rect: true
            },
            Label: {
                mount: 0.5,
                x: (w) => w / 2,
                y: (h) => h / 2,
                h: 28,
                text: {
                    textAlign: "center",
                    fontSize: 24,
                    textColor: Colors(theme.colors.text.surface).get()
                }
            },
            Icon: {
                mount: 0.5,
                x: (w) => w / 2 - 32 / 2,
                y: (h) => h / 2,
                h: 32,
                w: 32,
                colorValue: theme.colors.text.surface,
                type: MaterialIcon
            }
        };
    }

    _data: KeyData = {
        origin: "",
        key: "",
        label: "",
        action: ""
    };
    _labelText = {};
    _icon = "";
    // _activeIcon = "";

    readonly Icon = this.getByRef("Icon")!;
    readonly Label = this.getByRef("Label")!;
    readonly Background = this.getByRef("Background")!;

    static get width() {
        return theme.keyboard.w / 6;
    }

    static get height() {
        return theme.keyboard.w / 6;
    }

    set data(value: KeyData) {
        this._data = value;

        this._update();
    }

    get data() {
        return this._data;
    }

    set labelText(obj) {
        this._labelText = obj;
        this.Label.patch({ text: obj });
    }

    get labelText() {
        return this._labelText;
    }

    set label(obj) {
        // this.Label.patch(obj);
    }

    get label() {
        return this.tag("Label");
    }

    set icon(src: string) {
        this._icon = src;

        this._update();
    }

    get icon() {
        return this._icon;
    }

    _update() {
        if (!this.active) {
            return;
        }
        const { label = "" } = this._data;
        const hasFocus = this.hasFocus();

        this.Label.patch({
            text: { text: label === "Space" ? label : label }
        });

        this._setIcon();
        this._createBackground(hasFocus);

        this.Label.patch({
            y: this._icon || (!this._icon && !isNaN(parseInt(label))) ? (h) => h / 2 : (h) => h / 2 - 2
        });
    }

    override _active() {
        this._update();
    }

    override _init() {
        this._themeCallback = this._onThemeChange.bind(this);
        themeManager.subscribe(this._themeCallback);
    }

    _onThemeChange(customTheme: Theme | undefined) {
        if (!this._icon) {
            // Only update text color for non-icon keys
            this.Label.patch({
                text: {
                    textColor: customTheme?.title ?? Colors(theme.colors.text.surface).get()
                }
            });
        } else {
            // For icon keys, update the icon color by recreating the texture
            // this.Label.patch({
            //     texture: Lightning.Tools.getSvgTexture(Utils.asset(this._icon), 28, 28)
            // });
            // Set the color using the color property
            this.Label.color = customTheme?.title ?? Colors(theme.colors.text.surface).get();
        }

        this._createBackground(this.hasFocus());
    }

    override _detach() {
        themeManager.unsubscribe(this._themeCallback);
    }

    override _focus() {
        const customTheme = themeManager.getTheme();
        if (!this._icon) {
            this.Label.patch({
                text: {
                    textColor: customTheme?.title ?? Colors(theme.colors.text.primary).get()
                }
            });
        } else {
            this.Icon.patch({
                colorValue: theme.colors.text.primary
            });
        }
        this._createBackground(true);
    }

    override _unfocus() {
        const customTheme = themeManager.getTheme();
        if (!this._icon) {
            this.Label.patch({
                text: {
                    textColor: customTheme?.title ?? Colors(theme.colors.text.surface).get()
                }
            });
        } else {
            this.Icon.patch({
                colorValue: theme.colors.text.surface
            });
        }
        this._createBackground(false);
    }

    _setIcon() {
        if (this._icon) {
            this.Label.patch({
                text: {
                    text: ""
                }
            });
            this.Icon.patch({
                w: 32,
                icon: this._icon
            });
        }
    }

    _createBackground(isActive: boolean) {
        const customTheme = themeManager.getTheme();
        this.Background.patch({
            texture: Lightning.Tools.getRoundRect(
                this.w,
                this.w,
                theme.keyboard.radius,
                0,
                undefined,
                true,
                isActive
                    ? (customTheme?.interaction ?? Colors(theme.colors.focusAccent).get())
                    : Colors(theme.colors.bg.surface).get()
            )
        });
    }

    _handleClick() {
        this.fireAncestors("$focusKey", this._data.key);
    }
}
